<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
    import { autoUpdate, flip, offset, shift, useFloating, type Placement } from '@floating-ui/vue';
    import { temporaryUUID } from '../composables/useTemporaryUUID';

    const props = withDefaults(
        defineProps<{
            type?: 'text' | 'popover' | 'menu';
            position?:
                | 'top-start'
                | 'top-end'
                | 'bottom-start'
                | 'bottom-end'
                | 'right-start'
                | 'right-end'
                | 'left-start'
                | 'left-end';
            panelClass?: string;
        }>(),
        {
            type: 'popover',
            position: 'bottom-start',
        }
    );

    const panelId = 'echo-popover-' + temporaryUUID().replace('_', '');

    const reference = shallowRef<HTMLElement | null>(null);
    const floating = shallowRef<HTMLElement | null>(null);
    const open = ref(false);

    const buttonAttrs = computed(() => {
        const attrs: Record<string, unknown> = {
            'aria-expanded': open.value,
            'aria-controls': panelId,
        };
        if (props.type === 'menu') attrs['aria-haspopup'] = 'menu';
        else if (props.type === 'popover') attrs['aria-haspopup'] = 'dialog';
        return attrs;
    });

    const FOCUSABLE_SELECTOR = 'button, a, [tabindex]:not([tabindex="-1"])';

    const resolveFocusable = (el: HTMLElement): HTMLElement => {
        if (el.matches(FOCUSABLE_SELECTOR)) return el;
        const inner = el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        return inner ?? el;
    };

    const setReference = (el: unknown) => {
        if (!el) {
            reference.value = null;
            return;
        }
        if (el instanceof HTMLElement) {
            reference.value = resolveFocusable(el);
            return;
        }
        const vnode = el as { $el?: unknown };
        reference.value = vnode.$el instanceof HTMLElement ? resolveFocusable(vnode.$el) : null;
    };

    const { floatingStyles, isPositioned } = useFloating(reference, floating, {
        placement: computed(() => props.position as Placement),
        strategy: 'absolute',
        whileElementsMounted: autoUpdate,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
    });

    let restoreFocusOnClose = false;

    const togglePopover = () => {
        open.value = !open.value;
    };
    const close = () => {
        open.value = false;
    };

    const onDocPointerDown = (e: PointerEvent) => {
        const target = e.target as Node | null;
        if (!target) return;
        if (reference.value?.contains(target)) return;
        if (floating.value?.contains(target)) return;
        restoreFocusOnClose = false;
        close();
    };
    const onKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            restoreFocusOnClose = true;
            close();
        }
    };

    watch(open, (isOpen, wasOpen) => {
        if (isOpen) {
            if (props.type !== 'text') document.addEventListener('pointerdown', onDocPointerDown);
            document.addEventListener('keydown', onKeydown);
            if (props.type !== 'text') {
                nextTick(() => floating.value?.focus());
            }
        } else {
            document.removeEventListener('pointerdown', onDocPointerDown);
            document.removeEventListener('keydown', onKeydown);
            if (wasOpen && restoreFocusOnClose) reference.value?.focus();
            restoreFocusOnClose = false;
        }
    });
    onBeforeUnmount(() => {
        document.removeEventListener('pointerdown', onDocPointerDown);
        document.removeEventListener('keydown', onKeydown);
    });

    defineExpose({ close });
</script>

<template>
    <slot
        name="button"
        :anchor="setReference"
        :toggle-popover="togglePopover"
        :open="open"
        :button-attrs="buttonAttrs"
    />

    <Teleport v-if="type !== 'text'" to="body">
        <div
            v-if="open"
            :id="panelId"
            ref="floating"
            :class="[type === 'menu' ? 'Popover__menu' : 'Popover__popover', panelClass]"
            :style="[floatingStyles, { visibility: isPositioned ? 'visible' : 'hidden' }]"
            :role="type === 'menu' ? 'menu' : 'dialog'"
            tabindex="-1"
        >
            <slot name="popover" :close-popover="close" />
        </div>
    </Teleport>

    <div v-else-if="open" :id="panelId" class="Popover__text">
        <slot name="popover" :close-popover="close" />
    </div>
</template>
